import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, ChevronRight } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Mejores',
    titleHighlight: 'Guerreros',
    viewFullRankings: 'Ver Rankings Completos',
    rank: 'Rango',
    character: 'Personaje',
    class: 'Clase',
    level: 'Nivel',
    resets: 'Resets',
    topGuilds: 'Mejores Clanes',
    guild: 'Clan',
    master: 'Líder',
    members: 'Miembros',
    score: 'Puntos'
  },
  en: {
    title: 'Top',
    titleHighlight: 'Warriors',
    viewFullRankings: 'View Full Rankings',
    rank: 'Rank',
    character: 'Character',
    class: 'Class',
    level: 'Level',
    resets: 'Resets',
    topGuilds: 'Top Guilds',
    guild: 'Guild',
    master: 'Master',
    members: 'Members',
    score: 'Score'
  },
  pt: {
    title: 'Melhores',
    titleHighlight: 'Guerreiros',
    viewFullRankings: 'Ver Rankings Completos',
    rank: 'Rank',
    character: 'Personagem',
    class: 'Classe',
    level: 'Nível',
    resets: 'Resets',
    topGuilds: 'Melhores Clãs',
    guild: 'Clã',
    master: 'Líder',
    members: 'Membros',
    score: 'Pontos'
  }
};
const topPlayers = [{
  rank: 1,
  name: 'VANGUARD',
  class: 'Blade Knight',
  level: 400,
  resets: 150
}, {
  rank: 2,
  name: 'ESCANOR',
  class: 'Grand Master',
  level: 400,
  resets: 148
}, {
  rank: 3,
  name: 'BASTION',
  class: 'Dark Lord',
  level: 400,
  resets: 145
}, {
  rank: 4,
  name: 'LEONIDAS',
  class: 'Rage Fighter',
  level: 398,
  resets: 140
}, {
  rank: 5,
  name: 'SENTINEL',
  class: 'Dark Lord',
  level: 395,
  resets: 138
}];
const topGuilds = [{
  rank: 1,
  name: 'IMPERIUM',
  master: 'VANGUARD',
  members: 80,
  score: 15420
}, {
  rank: 2,
  name: 'LEGENDS',
  master: 'ESCANOR',
  members: 75,
  score: 14850
}, {
  rank: 3,
  name: 'TITANS',
  master: 'BASTION',
  members: 72,
  score: 13920
}, {
  rank: 4,
  name: 'SPARTANS',
  master: 'LEONIDAS',
  members: 68,
  score: 12750
}, {
  rank: 5,
  name: 'GUARDIANS',
  master: 'SENTINEL',
  members: 65,
  score: 11980
}];
export function Rankings() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section id="rankings" className="py-16 md:py-24 relative z-10 bg-darkerBlue overflow-hidden" data-id="element-246">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-248">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 flex items-center justify-center gap-4" data-id="element-249">
            <Trophy className="w-10 h-10 text-gold" data-id="element-250" />
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-251">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-252" />
        </div>

        {/* Two Column Layout - Top Players */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16" data-id="element-253">
          {/* Left: Animated Character */}
          <motion.div initial={{
          opacity: 0,
          x: -50,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          x: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="flex items-center justify-center" data-id="element-254">
            <div className="relative w-full max-w-lg lg:max-w-xl" data-id="element-255">
              <motion.img src="https://cdn.magicpatterns.com/uploads/vmSVXSm7qDFoDrzShhddTr/ChatGPT_Image_Mar_25,_2026,_06_24_09_PM.png" alt="MU Vanguard Gladiator" className="w-full h-auto object-contain drop-shadow-[0_0_30px_rgba(212,175,55,0.4)] md:drop-shadow-[0_0_60px_rgba(212,175,55,0.5)]" animate={{
              y: [0, -10, 0] // Reduced movement on mobile for smoother effect
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-256" />
              {/* Glow effect behind character */}
              <div className="absolute inset-0 bg-gradient-to-br from-gold/30 via-glowBlue/30 to-purple-500/20 rounded-full blur-3xl -z-10" data-id="element-257" />
            </div>
          </motion.div>

          {/* Right: Top Players Table */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-258">
            <div className="mb-6 flex justify-end" data-id="element-259">
              <motion.a initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} href="#full-rankings" className="flex items-center gap-2 text-gold hover:text-gold-light transition-colors font-cinzel font-bold group" data-id="element-260">
                {t.viewFullRankings}
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-261" />
              </motion.a>
            </div>

            <div className="glass-panel rounded-2xl overflow-hidden border border-white/10" data-id="element-262">
              <div className="overflow-x-auto" data-id="element-263">
                <table className="w-full text-left border-collapse" data-id="element-264">
                  <thead data-id="element-265">
                    <tr className="bg-darkBlue/80 border-b border-white/10 text-gray-400 font-cinzel text-sm uppercase tracking-wider" data-id="element-266">
                      <th className="p-5 font-semibold" data-id="element-267">{t.rank}</th>
                      <th className="p-5 font-semibold" data-id="element-268">{t.character}</th>
                      <th className="p-5 font-semibold" data-id="element-269">{t.class}</th>
                      <th className="p-5 font-semibold text-right" data-id="element-270">
                        {t.level}
                      </th>
                      <th className="p-5 font-semibold text-right" data-id="element-271">
                        {t.resets}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-272">
                    {topPlayers.map((player, index) => <tr key={player.rank} className="hover:bg-white/5 transition-colors group" data-id="element-273">
                        <td className="p-5" data-id="element-274">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold font-cinzel
                        ${index === 0 ? 'bg-gold text-darkerBlue shadow-[0_0_15px_rgba(212,175,55,0.5)]' : index === 1 ? 'bg-gray-300 text-darkerBlue' : index === 2 ? 'bg-[#CD7F32] text-darkerBlue' : 'bg-darkBlue text-gray-400 border border-white/10'}`} data-id="element-275">
                            {player.rank}
                          </div>
                        </td>
                        <td className="p-5 font-bold text-white group-hover:text-gold transition-colors" data-id="element-276">
                          {player.name}
                        </td>
                        <td className="p-5 text-gray-300" data-id="element-277">{player.class}</td>
                        <td className="p-5 text-right font-mono text-glowBlue" data-id="element-278">
                          {player.level}
                        </td>
                        <td className="p-5 text-right font-mono text-gold-light" data-id="element-279">
                          {player.resets}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Two Column Layout - Top Guilds */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-280">
          {/* Left: Top Guilds Table */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-281">
            <div className="mb-6" data-id="element-282">
              <h3 className="text-2xl md:text-3xl font-bold text-white font-cinzel" data-id="element-283">
                {t.topGuilds}
              </h3>
              <div className="w-16 h-1 bg-gold rounded-full mt-2" data-id="element-284" />
            </div>

            <div className="glass-panel rounded-2xl overflow-hidden border border-white/10" data-id="element-285">
              <div className="overflow-x-auto" data-id="element-286">
                <table className="w-full text-left border-collapse" data-id="element-287">
                  <thead data-id="element-288">
                    <tr className="bg-darkBlue/80 border-b border-white/10 text-gray-400 font-cinzel text-sm uppercase tracking-wider" data-id="element-289">
                      <th className="p-5 font-semibold" data-id="element-290">{t.rank}</th>
                      <th className="p-5 font-semibold" data-id="element-291">{t.guild}</th>
                      <th className="p-5 font-semibold" data-id="element-292">{t.master}</th>
                      <th className="p-5 font-semibold text-right" data-id="element-293">
                        {t.members}
                      </th>
                      <th className="p-5 font-semibold text-right" data-id="element-294">
                        {t.score}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-295">
                    {topGuilds.map((guild, index) => <tr key={guild.rank} className="hover:bg-white/5 transition-colors group" data-id="element-296">
                        <td className="p-5" data-id="element-297">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold font-cinzel
                        ${index === 0 ? 'bg-gold text-darkerBlue shadow-[0_0_15px_rgba(212,175,55,0.5)]' : index === 1 ? 'bg-gray-300 text-darkerBlue' : index === 2 ? 'bg-[#CD7F32] text-darkerBlue' : 'bg-darkBlue text-gray-400 border border-white/10'}`} data-id="element-298">
                            {guild.rank}
                          </div>
                        </td>
                        <td className="p-5 font-bold text-white group-hover:text-gold transition-colors" data-id="element-299">
                          {guild.name}
                        </td>
                        <td className="p-5 text-gray-300" data-id="element-300">{guild.master}</td>
                        <td className="p-5 text-right font-mono text-glowBlue" data-id="element-301">
                          {guild.members}
                        </td>
                        <td className="p-5 text-right font-mono text-gold-light" data-id="element-302">
                          {guild.score.toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Right: Animated Character */}
          <motion.div initial={{
          opacity: 0,
          x: 50,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          x: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="flex items-center justify-center" data-id="element-303">
            <div className="relative w-full max-w-lg lg:max-w-xl" data-id="element-304">
              <img src="https://cdn.magicpatterns.com/uploads/vvzqDPxJ1BFaR3XDime2bz/ChatGPT_Image_Mar_25,_2026,_08_05_16_PM.png" alt="MU Vanguard Dark Knight" className="w-full h-auto object-contain max-w-md mx-auto drop-shadow-[0_0_30px_rgba(212,175,55,0.4)] md:drop-shadow-[0_0_60px_rgba(212,175,55,0.5)]" data-id="element-305" />
              {/* Glow effect behind character */}
              <div className="absolute inset-0 bg-gradient-to-br from-gold/30 via-glowBlue/30 to-purple-500/20 rounded-full blur-3xl -z-10" data-id="element-306" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}